import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Dialog from '@react/react-spectrum/Dialog';
import Select from '@react/react-spectrum/Select';
import TextField from '@react/react-spectrum/Textfield';
import Datepicker from '@react/react-spectrum/Datepicker';
import { IconButton } from '@material-ui/core';
import FileCopyOutlined from '@material-ui/icons/FileCopyOutlined';

import './ClosedDiscountsTab.css';
import { OdDiscount, OdTemplate } from '../../../models/OdDiscount';
import { createOdDiscount, getOdDiscountTemplates } from '../../../providers/OdDiscountsAPI';
import { normalizeTimeToHms, toEndDateTimeString } from '../../../providers/FlexDiscountsAPI';
import { formatDate, getMarketSegmentOptions } from '../../../utils/Functions/utils';

const DEFAULT_EXPIRY_TIME = '23:59:59';

interface CreateOdDiscountDialogProps {
    readonly isOpen: boolean;
    readonly partnerId: string;
    readonly onCreated: () => void;
    readonly onCancel: () => void;
}

/** Maps sandbox-internal error tokens to partner-facing messages (HLD/LLD §19). */
function mapOdErrorReason(reason?: string, fallbackMessage?: string): string {
    switch (reason) {
        case 'RESOURCE_LIMIT_EXCEEDED':
            return 'You have reached your limit to create closed discount codes. Delete an existing code to create a new one.';
        case 'INVALID_FIELDS':
            return 'Please fill in all required fields.';
        case 'INVALID_REQUEST':
            return fallbackMessage || 'Please choose a future expiry date and a valid flex discount template.';
        default:
            return fallbackMessage || 'Failed to create closed discount code.';
    }
}

function normalizeSelectValue(value: string | string[] | number | number[]): string {
    return Array.isArray(value) ? String(value[0] ?? '') : String(value ?? '');
}

function CreateOdDiscountDialog(props: CreateOdDiscountDialogProps) {
    const { isOpen, partnerId, onCreated, onCancel } = props;

    const [marketSegment, setMarketSegment] = useState('COM');
    const [customerId, setCustomerId] = useState('');
    const [promotionId, setPromotionId] = useState('');
    const [expiryDate, setExpiryDate] = useState('');
    const [expiryTime, setExpiryTime] = useState(DEFAULT_EXPIRY_TIME);
    const [submitting, setSubmitting] = useState(false);
    const [validationError, setValidationError] = useState('');
    const [result, setResult] = useState<OdDiscount | null>(null);
    const [templates, setTemplates] = useState<OdTemplate[]>([]);
    const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);

    const marketSegmentOptions = getMarketSegmentOptions();

    const minExpiryDate = useMemo(() => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        return formatDate(tomorrow);
    }, []);

    const fetchTemplates = useCallback(async (segment: string) => {
        setIsLoadingTemplates(true);
        try {
            const response = await getOdDiscountTemplates(partnerId, segment);
            setTemplates(response.httpstatus === 200 ? response.data || [] : []);
        } catch (e) {
            console.error('Failed to fetch flex discount templates for the create dialog', e);
            setTemplates([]);
        } finally {
            setIsLoadingTemplates(false);
        }
    }, [partnerId]);

    useEffect(() => {
        if (isOpen && marketSegment) {
            fetchTemplates(marketSegment);
        }
    }, [isOpen, marketSegment, fetchTemplates]);

    const promotionOptions = useMemo(
        () => templates.map((t) => ({ label: t.label, value: t.id })),
        [templates]
    );

    const resetState = () => {
        setMarketSegment('COM');
        setCustomerId('');
        setPromotionId('');
        setExpiryDate('');
        setExpiryTime(DEFAULT_EXPIRY_TIME);
        setValidationError('');
        setResult(null);
        setTemplates([]);
    };

    const handleCancel = () => {
        resetState();
        onCancel();
    };

    const handleDone = () => {
        resetState();
        onCreated();
    };

    const handleSubmit = async () => {
        if (!customerId.trim()) {
            setValidationError('Please enter a customer ID.');
            return;
        }
        if (!promotionId) {
            setValidationError('Please select a flex discount template.');
            return;
        }
        if (!expiryDate || expiryDate < minExpiryDate) {
            setValidationError('Please choose a future expiry date.');
            return;
        }

        setValidationError('');
        setSubmitting(true);
        try {
            const response = await createOdDiscount(partnerId, {
                customerId: customerId.trim(),
                promotionId,
                marketSegment,
                endDate: toEndDateTimeString(expiryDate, expiryTime)
            });

            if ((response.httpstatus === 200 || response.httpstatus === 201) && response.data) {
                setResult(response.data);
            } else {
                setValidationError(mapOdErrorReason(response.reason, response.message));
            }
        } catch (e) {
            const err = e as any;
            setValidationError(err?.message || 'Failed to create closed discount code.');
        } finally {
            setSubmitting(false);
        }
    };

    let confirmLabel: string;
    if (result) {
        confirmLabel = 'Done';
    } else if (submitting) {
        confirmLabel = 'Creating...';
    } else {
        confirmLabel = 'Create';
    }

    let dialogTitle: string;
    if (result) {
        dialogTitle = result.reused ? 'Existing Code Returned' : 'Closed Discount Code Created';
    } else {
        dialogTitle = 'Create Closed Discount Code';
    }

    return (
        <Dialog
            className="create-od-discount-dialog"
            title={dialogTitle}
            open={isOpen}
            confirmLabel={confirmLabel}
            cancelLabel={result ? undefined : 'Cancel'}
            onConfirm={result ? handleDone : handleSubmit}
            onCancel={handleCancel}
        >
            {result ? (
                <div>
                    {result.reused && (
                        <div className="create-od-discount-reused-note">
                            A closed discount code already exists for this customer and flex discount — see the
                            existing active code and customer ID below.
                        </div>
                    )}
                    <div className="create-od-discount-code-row">
                        <span>
                            Customer ID: <strong>{result.customerId}</strong>
                        </span>
                        <IconButton className="copy-icon" onClick={() => navigator.clipboard.writeText(result.customerId)}>
                            <FileCopyOutlined className="tiny-icon" style={{ fontSize: '1rem' }} />
                        </IconButton>
                    </div>
                    <div className="create-od-discount-code-row">
                        <span>
                            Code: <strong>{result.code}</strong>
                        </span>
                        <IconButton className="copy-icon" onClick={() => navigator.clipboard.writeText(result.code)}>
                            <FileCopyOutlined className="tiny-icon" style={{ fontSize: '1rem' }} />
                        </IconButton>
                    </div>
                </div>
            ) : (
                <div>
                    <div>
                        Market Segment:&nbsp;
                        <Select
                            aria-label="Market Segment"
                            value={marketSegment}
                            options={marketSegmentOptions}
                            onChange={(value) => {
                                setMarketSegment(normalizeSelectValue(value));
                                setPromotionId('');
                            }}
                        />
                    </div>
                    <br />
                    <div>
                        Customer ID:&nbsp;
                        <TextField
                            name="customerId"
                            placeholder="Enter customer ID"
                            value={customerId}
                            onChange={(value) => setCustomerId(value)}
                        />
                    </div>
                    <br />
                    <div>
                        Flex Discount Template:&nbsp;
                        <Select
                            aria-label="Discount Template"
                            value={promotionId}
                            options={promotionOptions}
                            disabled={isLoadingTemplates}
                            placeholder={isLoadingTemplates ? 'Loading templates...' : 'Select a flex discount template'}
                            onChange={(value) => setPromotionId(normalizeSelectValue(value))}
                        />
                    </div>
                    <br />
                    <div>
                        Expiry Date:&nbsp;
                        <Datepicker
                            value={expiryDate}
                            min={minExpiryDate}
                            onChange={(value) => {
                                if (typeof value === 'string') {
                                    setExpiryDate(value);
                                }
                            }}
                            selectionType="single"
                            type="date"
                        />
                    </div>
                    <br />
                    <div>
                        Expiry Time:&nbsp;
                        <div className="create-od-discount-time-picker-wrapper">
                            <input
                                type="time"
                                step={1}
                                value={normalizeTimeToHms(expiryTime)}
                                onChange={(e) => setExpiryTime(normalizeTimeToHms(e.target.value))}
                                className="create-od-discount-time-native"
                                aria-label="Expiry Time"
                            />
                        </div>
                    </div>
                    {validationError && <div className="create-od-discount-error">{validationError}</div>}
                </div>
            )}
        </Dialog>
    );
}

export default CreateOdDiscountDialog;
